import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SpecialCharacters } from "../../Constants/DataModelToolConstants";
import { actionViewerSliceDispatch, apiResponseModalSliceDispatch, pickAFileSliceDispatch, questionViewerSliceDispatch, ruleViewerSliceDispatch } from "../../Constants/ReduxConstants";
import { ReduxHelpers } from "../../Helpers/ReduxHelpers";

interface RootState {
    refreshLoadedFile: { isLoading: boolean };
    pickAFile: { fileLength: number | null };
}

const PickAFile: React.FC = () => {
    const isLoading = useSelector((state: RootState) => state.refreshLoadedFile.isLoading);
    const fileLength = useSelector((state: RootState) => state.pickAFile.fileLength);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const dispatch = useDispatch();

    const { CreateContentsFromStates } = ReduxHelpers();

    function handleButtonClick() {
        fileInputRef.current?.click();
    }

    async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (e) => {
            const values = e.target?.result as string;
            if (!values) return;
            
            const fileName = file.name;

                let questions = JSON.parse(values)["questions"]
                let serviceQuestions = JSON.parse(values)["serviceQuestions"]
                let serviceQuestionsBrand = JSON.parse(values)["serviceBrandQuestions"]
                let rules = JSON.parse(values)["rules"]

                let allServiceIds = serviceQuestions.map(m => m.questionId);
                let allServiceBrandIds = serviceQuestionsBrand.map(m => m.questionId)

                let combinedIds = allServiceIds.concat(allServiceBrandIds)
                let uniqueServiceIds = Array.from(new Set(combinedIds))

                let allQuestions = questions.map(m => m.questionId)

                let missingQuestions = allQuestions.filter(x => !uniqueServiceIds.includes(x))
                let missingQuestionsDetails = questions.filter(q => missingQuestions.includes(q.questionId))
                let missingQuestionDataKeys = missingQuestionsDetails.map(q => q.dataKey)

                const duplicateDataKeys = questions.map(x => x.dataKey).filter((item, index) => questions.map(x => x.dataKey).indexOf(item) !== index)
                let specialCharacters = questions.filter((item) => SpecialCharacters.some(y => item.dataKey.includes(y))).map(z => z.dataKey);

                const duplicateRuleEventTypes = rules.map(x => x.event.type).filter((item, index) => rules.map(x => x.event.type).indexOf(item) !== index);

                if (duplicateDataKeys?.length > 0) {
                    alert("There are duplicate data keys for the questions. Please select the 'Close File Without Saving' button. The following popup data keys are duplicates.")
                    duplicateDataKeys.forEach(dup => alert(dup))
                }
                else if (specialCharacters?.length > 0) {
                    alert("Unable to download file from Azure due to corrupted data caused by special characters in the data names for the questions. The following popup data keys are include special characters.")
                    specialCharacters.forEach(specialCharacter => alert(specialCharacter))
                }
                else if (missingQuestionDataKeys?.length > 0) {
                    alert("The following question(s) are missing associated services, and will not appear on the DE form previewer: " +
                        "\n\n" + missingQuestionDataKeys.join('\n'))
                }
                else if (duplicateRuleEventTypes?.length > 0) {
                    alert("There are duplicate event types for the rules. Please select the 'Close File Without Saving' button. The following popup event types are duplicates.")
                    duplicateRuleEventTypes.forEach(dup => alert(dup))
                }

            const parsedValues = JSON.parse(values);
            dispatch({ type: pickAFileSliceDispatch.setQuestions, payload: questions });
            dispatch({ type: questionViewerSliceDispatch.setQuestionSearchResults, payload: questions });

            dispatch({ type: pickAFileSliceDispatch.setFileLength, payload: values.length });

            dispatch({ type: pickAFileSliceDispatch.setFileContents, payload: values });

            dispatch({ type: pickAFileSliceDispatch.setFileName, payload: fileName });
            dispatch({ type: pickAFileSliceDispatch.setFilePath, payload: fileName });

            dispatch({ type: pickAFileSliceDispatch.setActions, payload: parsedValues.actions });
            dispatch({ type: actionViewerSliceDispatch.setActionSearchResults, payload: parsedValues.actions });

            dispatch({ type: pickAFileSliceDispatch.setAddressStart, payload: parsedValues.addressStart });

            dispatch({ type: pickAFileSliceDispatch.setAddressEnd, payload: parsedValues.addressEnd });

            dispatch({ type: pickAFileSliceDispatch.setAssistanceFormId, payload: parsedValues.assistanceFormId });

            dispatch({ type: pickAFileSliceDispatch.setBaseName, payload: parsedValues.baseName });

            dispatch({ type: pickAFileSliceDispatch.setBusinessRules, payload: parsedValues.businessRules });

            dispatch({ type: pickAFileSliceDispatch.setBrandList, payload: parsedValues.brandList });

            dispatch({ type: pickAFileSliceDispatch.setFormDescription, payload: parsedValues.formDescription });

            dispatch({ type: pickAFileSliceDispatch.setFormName, payload: parsedValues.formName });

            dispatch({ type: pickAFileSliceDispatch.setFormRuleSetId, payload: parsedValues.formRuleSetId });

            dispatch({ type: pickAFileSliceDispatch.setInterfaceId, payload: parsedValues.interfaceId });

            dispatch({ type: pickAFileSliceDispatch.setLayout, payload: parsedValues.layout });

            dispatch({ type: pickAFileSliceDispatch.setRules, payload: parsedValues.rules });
            dispatch({ type: ruleViewerSliceDispatch.setRuleSearchResults, payload: rules });

            dispatch({ type: pickAFileSliceDispatch.setServiceBrandQuestions, payload: parsedValues.serviceBrandQuestions });

            dispatch({ type: pickAFileSliceDispatch.setServiceList, payload: parsedValues.serviceList });

            dispatch({ type: pickAFileSliceDispatch.setServiceQuestions, payload: parsedValues.serviceQuestions });

            const tempBrandDropDownList = parsedValues.brandList.map((b: any) => {
                return ({ key: b.brandId, label: b.brandName, value: b.brandId });
            });

            dispatch({ type: pickAFileSliceDispatch.setBrandDropDownList, payload: tempBrandDropDownList });

            const tempServiceDropDownList = parsedValues.serviceList.map((b: any) => {
                return ({ key: b.serviceId, label: b.serviceName, value: b.serviceId });
            });

            dispatch({ type: pickAFileSliceDispatch.setServiceDropDownList, payload: tempServiceDropDownList });

            dispatch({ type: pickAFileSliceDispatch.setHasMultipleServices, payload: tempServiceDropDownList.length > 1 });
            dispatch({ type: pickAFileSliceDispatch.setHasMultipleBrands, payload: tempBrandDropDownList.length > 1 });

            dispatch({ type: apiResponseModalSliceDispatch.setOpenedFileFromApi, payload: false });

            CreateContentsFromStates(parsedValues);
        };
        reader.readAsText(file);
        
        // Reset the input so the same file can be selected again
        event.target.value = '';
    }

    return (
        <>
            <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleFileChange} 
                accept=".json" 
                style={{ display: 'none' }} 
            />
            <button 
                disabled={fileLength != null || isLoading} 
                onClick={handleButtonClick} 
                title="Pick A File"
            >
                Pick A File
            </button>
        </>
    );
};

export default PickAFile;